import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import {
  Campo,
  type CategoriaGasto,
  type CuentaContable,
  type TipoGasto,
} from "@/paginas/interno/Gastos"

/**
 * Las categorías de gasto: el agrupador de los informes —«Servicios básicos»,
 * «Personal», «Oficina»—.
 *
 * ⚠ **Se archivan, no se borran.** Una categoría borrada dejaría gastos y órdenes
 * históricas sin agrupar, y el informe del año pasado cambiaría solo. Archivada sale
 * de los selectores y sigue nombrando lo que ya la usaba.
 *
 * ⚠ **También se crean desde el desplegable del gasto y de la orden**, que es donde
 * hacen falta. Esta pestaña es para lo que allí no se hace: renombrar, cambiar su
 * tipo y archivar.
 *
 * ⚠ **Cada categoría cuelga de una partida del PLAN DE CUENTAS** (28/10, traído del 2.0).
 * La orden de pago no guarda su cuenta: la lee de su categoría, así que cambiarla aquí
 * reclasifica todo el histórico de la categoría.
 */
export function CategoriasGasto() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<CategoriaGasto | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "gasto:administrar")

  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
  })
  const plan = useQuery({
    queryKey: ["plan-cuentas"],
    queryFn: () => api.get<CuentaContable[]>("/api/v1/plan-cuentas"),
    staleTime: 10 * 60_000,
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      enEdicion
        ? api.patch(`/api/v1/categorias-gasto/${enEdicion.id}`, cuerpo)
        : api.post("/api/v1/categorias-gasto", cuerpo),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["categorias-gasto"] })
      clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
      setAbierto(false)
    },
  })

  function abrir(categoria: CategoriaGasto | null) {
    guardar.reset()
    setEnEdicion(categoria)
    setAbierto(true)
  }

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const descripcion = String(datos.get("descripcion") ?? "").trim()
    const cuerpo: Record<string, unknown> = {
      nombre: String(datos.get("nombre") ?? "").trim(),
      descripcion: descripcion === "" ? null : descripcion,
      tipo_gasto: String(datos.get("tipo_gasto") ?? "VARIABLE") as TipoGasto,
    }
    const cuenta = String(datos.get("cuenta_id") ?? "")
    if (cuenta !== "") cuerpo.cuenta_id = Number(cuenta)
    if (enEdicion) cuerpo.activo = datos.get("activo") === "on"
    guardar.mutate(cuerpo)
  }

  if (categorias.isPending) return <p className="py-6">Cargando…</p>
  if (categorias.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar las categorías.
      </p>
    )
  }

  const filas = categorias.data
  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <section className="space-y-4">
      {puedeAdministrar && (
        <div className="flex justify-end">
          <Boton variante="primario" onClick={() => abrir(null)}>
            <Plus size={16} /> Nueva categoría
          </Boton>
        </div>
      )}

      <Tabla etiqueta="Categorías de gasto">
        <Cabecera>
          <Th>Categoría</Th>
          <Th>Cuenta</Th>
          <Th>Descripción</Th>
          <Th>Tipo</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && (
            <TablaVacia colSpan={5}>
              Todavía no hay categorías. También se crean desde el desplegable de un gasto.
            </TablaVacia>
          )}
          {filas.map((categoria) => (
            <Fila
              key={categoria.id}
              atenuada={!categoria.activo}
              className={puedeAdministrar ? "cursor-pointer" : undefined}
              onClick={puedeAdministrar ? () => abrir(categoria) : undefined}
            >
              <Td>{categoria.nombre}</Td>
              <Td className="tabular-nums">
                {categoria.cuenta ? (
                  categoria.cuenta.etiqueta
                ) : (
                  <Insignia tono="aviso">Sin cuenta</Insignia>
                )}
              </Td>
              <Td style={{ color: "var(--fg-muted)" }}>{categoria.descripcion ?? "—"}</Td>
              <Td>
                <Insignia tono={categoria.tipo_gasto === "FIJO" ? "info" : "neutro"}>
                  {categoria.tipo_gasto === "FIJO" ? "Fijo" : "Variable"}
                </Insignia>
              </Td>
              <Td>
                {categoria.activo ? (
                  <Insignia tono="exito">Activa</Insignia>
                ) : (
                  <Insignia tono="neutro">Archivada</Insignia>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      <Modal
        abierto={abierto}
        titulo={enEdicion ? `Editar ${enEdicion.nombre}` : "Nueva categoría"}
        ancho="sm"
        onCerrar={() => setAbierto(false)}
      >
        <form key={enEdicion?.id ?? "nueva"} className="space-y-4" onSubmit={enviar}>
          <Campo para="categoria-nombre" etiqueta="Nombre">
            <input
              id="categoria-nombre"
              name="nombre"
              required
              minLength={2}
              maxLength={80}
              defaultValue={enEdicion?.nombre ?? ""}
              className="campo mt-1"
            />
          </Campo>
          <Campo para="categoria-descripcion" etiqueta="Descripción">
            <input
              id="categoria-descripcion"
              name="descripcion"
              maxLength={200}
              defaultValue={enEdicion?.descripcion ?? ""}
              className="campo mt-1"
            />
          </Campo>
          <Campo
            para="categoria-tipo"
            etiqueta="Tipo"
            ayuda="Fijo se repite cada periodo; variable aparece cuando hace falta."
          >
            <select
              id="categoria-tipo"
              name="tipo_gasto"
              defaultValue={enEdicion?.tipo_gasto ?? "VARIABLE"}
              className="campo mt-1"
            >
              <option value="FIJO">Fijo</option>
              <option value="VARIABLE">Variable</option>
            </select>
          </Campo>
          <Campo
            para="categoria-cuenta"
            etiqueta="Cuenta del plan"
            ayuda="La partida contable de todo lo que se gasta en esta categoría."
          >
            <select
              id="categoria-cuenta"
              name="cuenta_id"
              defaultValue={enEdicion?.cuenta_id ?? ""}
              className="campo mt-1"
            >
              <option value="">Sin cuenta</option>
              {(plan.data ?? [])
                .filter((c) => c.activo && c.tipo !== "INGRESO")
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.etiqueta}
                  </option>
                ))}
            </select>
          </Campo>
          {enEdicion && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="activo" defaultChecked={enEdicion.activo} />
              Activa (desmárcala para archivarla)
            </label>
          )}

          {error && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={guardar.isPending}>
              {guardar.isPending ? "Guardando…" : "Guardar"}
            </Boton>
          </div>
        </form>
      </Modal>
    </section>
  )
}
